/**
 * Tile — the surface every piece of content in the app sits on.
 *
 * It is a punchboard tile: a card with a cut nick at each corner (radial
 * gradients painted in the ground colour, so the shape reads as punched out of
 * a sheet rather than merely rounded) and a thin colour bar on the leading edge
 * carrying state. All colour comes from tokens in `styles.css`.
 */

import type { ComponentPropsWithoutRef, ElementType, KeyboardEvent, ReactNode } from "react";

/**
 * State carried by the edge bar. Deliberately named after roles rather than
 * hues, and never used as the ONLY signal — pair it with a label, since a
 * five-player group has a good chance of containing a colour-deficient rater.
 */
export type TileEdge = "none" | "spice" | "slate" | "ochre" | "rust" | "ok" | "muted";

export interface TileOwnProps {
  /**
   * Slot above the title — box art, a thumbnail, a mark. Present only to allow
   * the art-first arrangement; leaving it out keeps the original ordering, so
   * every tile already written renders unchanged. It carries no wrapper and no
   * class of its own: what goes in here is styled by the screen that owns it.
   */
  lead?: ReactNode;
  /** Rendered as the uppercase mono heading line inside the tile. */
  title?: ReactNode;
  /** Small dim line under the title — counts, dates, player names. */
  meta?: ReactNode;
  edge?: TileEdge;
  /** Recessed variant, for a tile that is a container rather than an action. */
  sunk?: boolean;
  /** Adds the press affordance. Implied when `onClick`, `href` or `to` is present. */
  interactive?: boolean;
  children?: ReactNode;
}

export type TileProps<T extends ElementType = "div"> = TileOwnProps & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof TileOwnProps | "as">;

/** The props of the rendered element that decide pressability and operability. */
interface TileElementProps {
  onClick?: unknown;
  onKeyDown?: ((event: KeyboardEvent<HTMLElement>) => void) | undefined;
  href?: unknown;
  /** The router `Link`'s destination prop — its `href` equivalent. */
  to?: unknown;
  role?: string | undefined;
  tabIndex?: number | undefined;
}

/**
 * Exported for tests. `as={Link}` takes `to`, not `href`, so a tile that is a
 * link is only detectable through it. An explicit `interactive` always wins,
 * including `interactive={false}` on a tile that does have a handler.
 */
export function inferPressable(props: TileElementProps & { interactive?: boolean }): boolean {
  const { interactive, onClick, href, to } = props;
  return interactive ?? Boolean(onClick ?? href ?? to);
}

/**
 * True when the browser already gives the element focus and Enter/Space for
 * free. A component (`as={Link}`) is taken to render an anchor when it carries
 * a destination; anything else pressable has to be given the keyboard contract
 * by hand.
 */
function isNativelyOperable(component: ElementType, props: TileElementProps): boolean {
  if (typeof component !== "string") return props.to !== undefined || props.href !== undefined;
  if (component === "button") return true;
  return component === "a" && props.href !== undefined;
}

export function Tile<T extends ElementType = "div">(props: TileProps<T>): ReactNode {
  const {
    as,
    lead,
    title,
    meta,
    edge = "none",
    sunk = false,
    interactive,
    children,
    className,
    ...rest
  } = props as TileProps<"div"> & { as?: ElementType };

  const Component = (as ?? "div") as ElementType;
  const attrs = rest as TileElementProps;
  const pressable = inferPressable({
    interactive,
    onClick: attrs.onClick,
    href: attrs.href,
    to: attrs.to,
  });

  // A pressable `div` is invisible to the keyboard and announces itself as
  // nothing, so it gets the button contract. The focus ring comes from the
  // global `:focus-visible` rule in `styles.css`.
  const operable =
    pressable && !isNativelyOperable(Component, attrs)
      ? {
          role: attrs.role ?? "button",
          tabIndex: attrs.tabIndex ?? 0,
          onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
            attrs.onKeyDown?.(event);
            if (event.defaultPrevented) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            // Space would scroll the page; the click is raised on the element
            // itself so the caller's `onClick` sees a real mouse event.
            event.preventDefault();
            event.currentTarget.click();
          },
        }
      : null;

  return (
    <Component
      {...rest}
      {...operable}
      className={className ? `pb-tile ${className}` : "pb-tile"}
      data-edge={edge}
      data-sunk={sunk ? "true" : undefined}
      data-interactive={pressable ? "true" : undefined}
    >
      {lead}
      {title !== undefined && <div className="pb-tile__title">{title}</div>}
      {meta !== undefined && <div className="pb-tile__meta">{meta}</div>}
      {children}
    </Component>
  );
}

/** Vertical rhythm helper for stacking content inside a tile. */
export function TileStack({ children }: { children: ReactNode }): ReactNode {
  return <div className="pb-tile__stack">{children}</div>;
}
